/**
 * Multi-Tier Cache Manager with LRU Eviction
 *
 * Implements smart caching strategy:
 * - Hot cache (sessionStorage): Currently active items for instant access
 * - Warm cache (localStorage): Recently used items with LRU eviction
 * - Site theme: Persistent, never evicted
 * - Profile/Community themes: 20 most frequently/recently visited
 */

// Cache version - increment when cache structure or transformation logic changes
const PROFILE_CACHE_VERSION = 2;
const COMMUNITY_CACHE_VERSION = 2;

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  visits: number;
  lastVisit: number;
  version?: number; // Add version for cache invalidation
}

interface CacheConfig {
  storage: 'sessionStorage' | 'localStorage';
  ttl: number; // Time to live in milliseconds
  maxEntries: number;
}

type CacheType = 'site' | 'profile' | 'community';

const CACHE_CONFIG: Record<CacheType, CacheConfig> = {
  site: {
    storage: 'sessionStorage',
    ttl: Infinity, // Site theme never expires in session
    maxEntries: 1
  },
  profile: {
    storage: 'localStorage',
    ttl: 7 * 24 * 60 * 60 * 1000, // 7 days
    maxEntries: 20
  },
  community: {
    storage: 'localStorage',
    ttl: 7 * 24 * 60 * 60 * 1000, // 7 days
    maxEntries: 20
  }
};

class CacheManager {
  /**
   * Get theme from cache with tier fallback
   * Priority: sessionStorage (hot) → localStorage (warm)
   */
  get(type: 'profile' | 'community', id: string): any | null {
    const expectedVersion = type === 'profile' ? PROFILE_CACHE_VERSION : COMMUNITY_CACHE_VERSION;

    // 1. Check hot cache (sessionStorage) - instant access
    const sessionKey = `${type}_${id}`;
    const sessionData = sessionStorage.getItem(sessionKey);
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        // Check version
        if (parsed.version && parsed.version !== expectedVersion) {
                    sessionStorage.removeItem(sessionKey);
        } else {
                    return parsed;
        }
      } catch (e) {
        sessionStorage.removeItem(sessionKey);
      }
    }

    // 2. Check warm cache (localStorage) - promote to hot if found
    const config = CACHE_CONFIG[type];
    const localKey = `${type}Cache`;
    const cache = this.getLocalCache(localKey);

    if (cache[id] && !this.isExpired(cache[id], config.ttl)) {
      // Check version
      if (cache[id].version && cache[id].version !== expectedVersion) {
                delete cache[id];
        localStorage.setItem(localKey, JSON.stringify(cache));
        return null;
      }

            // Promote to hot cache
      sessionStorage.setItem(sessionKey, JSON.stringify(cache[id].data));
      // Update access stats
      this.incrementVisit(localKey, id);
      return cache[id].data;
    }

        return null;
  }

  /**
   * Save theme to both cache tiers
   * Hot cache: immediate access for current session
   * Warm cache: persistent with LRU eviction
   */
  set(type: 'profile' | 'community', id: string, data: any): void {
    const version = type === 'profile' ? PROFILE_CACHE_VERSION : COMMUNITY_CACHE_VERSION;

    // Add version to data
    const versionedData = { ...data, version };

    // Always save to hot cache (sessionStorage)
    const sessionKey = `${type}_${id}`;
    sessionStorage.setItem(sessionKey, JSON.stringify(versionedData));

    // Save to warm cache (localStorage) with LRU management
    const config = CACHE_CONFIG[type];
    const localKey = `${type}Cache`;
    const cache = this.getLocalCache(localKey);

    cache[id] = {
      data: versionedData,
      timestamp: Date.now(),
      visits: (cache[id]?.visits || 0) + 1,
      lastVisit: Date.now(),
      version // Store version in cache entry too
    };

    // Evict least recently/frequently used if over limit
    if (Object.keys(cache).length > config.maxEntries) {
      const evicted = this.evictLRU(cache, config.maxEntries);
            localStorage.setItem(localKey, JSON.stringify(evicted));
    } else {
      localStorage.setItem(localKey, JSON.stringify(cache));
    }

      }

  /**
   * LRU eviction algorithm
   * Score = 70% visit frequency + 30% recency
   * Keeps most valuable entries (frequently AND recently accessed)
   */
  private evictLRU(cache: Record<string, CacheEntry<any>>, maxEntries: number): Record<string, CacheEntry<any>> {
    const now = Date.now();
    const sorted = Object.entries(cache).sort((a, b) => {
      // Higher score = more valuable = keep
      const scoreA = a[1].visits * 0.7 + ((now - a[1].lastVisit) / 1000000) * -0.3;
      const scoreB = b[1].visits * 0.7 + ((now - b[1].lastVisit) / 1000000) * -0.3;
      return scoreB - scoreA; // Descending (highest score first)
    });

    // Keep top N entries
    return Object.fromEntries(sorted.slice(0, maxEntries));
  }

  /**
   * Check if cache entry has expired
   */
  private isExpired(entry: CacheEntry<any>, ttl: number): boolean {
    if (ttl === Infinity) return false;
    return Date.now() - entry.timestamp > ttl;
  }

  /**
   * Get localStorage cache safely
   */
  private getLocalCache(key: string): Record<string, CacheEntry<any>> {
    try {
      const cached = localStorage.getItem(key);
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      console.warn(`⚠️ [CacheManager] Failed to read ${key}:`, e);
      return {};
    }
  }

  /**
   * Increment visit count and update last visit time
   */
  private incrementVisit(localKey: string, id: string): void {
    try {
      const cache = this.getLocalCache(localKey);
      if (cache[id]) {
        cache[id].visits++;
        cache[id].lastVisit = Date.now();
        localStorage.setItem(localKey, JSON.stringify(cache));
      }
    } catch (e) {
      console.warn(`⚠️ [CacheManager] Failed to increment visit:`, e);
    }
  }

  /**
   * Clear specific cache entry
   */
  clear(type: 'profile' | 'community', id: string): void {
    // Clear from hot cache
    sessionStorage.removeItem(`${type}_${id}`);

    // Clear from warm cache
    const localKey = `${type}Cache`;
    const cache = this.getLocalCache(localKey);
    delete cache[id];
    localStorage.setItem(localKey, JSON.stringify(cache));

      }

  /**
   * Clear all profile or community caches
   */
  clearType(type: 'profile' | 'community'): void {
    // Clear hot cache
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith(`${type}_`)) {
        sessionStorage.removeItem(key);
      }
    });

    // Clear warm cache
    localStorage.removeItem(`${type}Cache`);

      }

  /**
   * Clear all caches (site, profile, community)
   */
  clearAll(): void {
    // Clear site theme
    sessionStorage.removeItem('siteCustomization');

    // Clear all profiles
    this.clearType('profile');

    // Clear all communities
    this.clearType('community');

      }

  /**
   * Get cache statistics for UI display
   */
  getStats() {
    const profileCache = this.getLocalCache('profileCache');
    const communityCache = this.getLocalCache('communityCache');

    const profileEntries = Object.values(profileCache);
    const communityEntries = Object.values(communityCache);

    return {
      profiles: {
        count: profileEntries.length,
        totalVisits: profileEntries.reduce((sum, entry) => sum + entry.visits, 0),
        avgVisits: profileEntries.length > 0
          ? Math.round(profileEntries.reduce((sum, entry) => sum + entry.visits, 0) / profileEntries.length)
          : 0,
        oldest: profileEntries.length > 0
          ? Math.min(...profileEntries.map(e => e.timestamp))
          : null,
        newest: profileEntries.length > 0
          ? Math.max(...profileEntries.map(e => e.timestamp))
          : null
      },
      communities: {
        count: communityEntries.length,
        totalVisits: communityEntries.reduce((sum, entry) => sum + entry.visits, 0),
        avgVisits: communityEntries.length > 0
          ? Math.round(communityEntries.reduce((sum, entry) => sum + entry.visits, 0) / communityEntries.length)
          : 0,
        oldest: communityEntries.length > 0
          ? Math.min(...communityEntries.map(e => e.timestamp))
          : null,
        newest: communityEntries.length > 0
          ? Math.max(...communityEntries.map(e => e.timestamp))
          : null
      }
    };
  }

  /**
   * Get detailed cache entries for debugging
   */
  getDetailedStats() {
    const profileCache = this.getLocalCache('profileCache');
    const communityCache = this.getLocalCache('communityCache');

    return {
      profiles: Object.entries(profileCache).map(([id, entry]) => ({
        id,
        visits: entry.visits,
        age: Math.floor((Date.now() - entry.timestamp) / 1000 / 60 / 60), // hours
        lastVisit: Math.floor((Date.now() - entry.lastVisit) / 1000 / 60) // minutes
      })),
      communities: Object.entries(communityCache).map(([id, entry]) => ({
        id,
        visits: entry.visits,
        age: Math.floor((Date.now() - entry.timestamp) / 1000 / 60 / 60), // hours
        lastVisit: Math.floor((Date.now() - entry.lastVisit) / 1000 / 60) // minutes
      }))
    };
  }
}

// Export singleton instance
export const cacheManager = new CacheManager();

// Export types
export type { CacheEntry, CacheConfig, CacheType };
